import styled from '@emotion/styled';
import React, { useId } from 'react';
import { i18n, mediaQuery, sanitizeHtml } from 'src/common';
import { Coverage } from 'src/common/apiGraphql/types/types';
import { Text, Card, Flex, Tag, CheckboxInput } from 'src/components';
import { RadioButton } from '@kompare/kompare-ui-library';
import { onEnterKey } from 'src/common/style/a11y.style';
import { baseWidths, colors } from 'src/theme';

interface AdditionalCoverageFormProps {
  coverages: Coverage[];
  selectedCoverages: Array<{ code: string; variant: string }>;
  onCoverageChange: (selectedCoverages: Array<{ code: string; variant: string }>) => void;
}

const Container = styled(Card)({
  width: '100%',
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  [mediaQuery('laptop')]: {
    width: `${baseWidths.mobile}px`
  },
})

const CoverageDescription = styled(Text)({
  marginLeft: 48,
})

const Fieldset = styled.fieldset({
  border: 0,
  margin: 0,
  padding: 0,
  minWidth: 0,
  marginLeft: 48,
})

const Legend = styled.legend({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
})

const RadioButtonContainer = styled(Flex)({
  gap: 16,
  flexDirection: 'column',
  [mediaQuery('laptop')]: {
    flexDirection: 'row',
  },
})

export const AdditionalCoverageForm: React.FC<AdditionalCoverageFormProps> = ({
  coverages,
  selectedCoverages,
  onCoverageChange
}) => {
  const idPrefix = useId();
  const handleCoverageToggle = (coverageCode: string) => {
    const isSelected = selectedCoverages.some(c => c.code === coverageCode);
    let updated;
    
    if (isSelected) {
      // Remove coverage
      updated = selectedCoverages.filter(c => c.code !== coverageCode);
    } else {
      // Add coverage with default variant
      const coverage = coverages.find(c => c.code === coverageCode);
      const defaultVariant = coverage?.variants?.[0]?.code || '';
      updated = [...selectedCoverages, { code: coverageCode, variant: defaultVariant }];
    }
    
    onCoverageChange(updated);
  };

  const handleVariantChange = (coverageCode: string, variantCode: string) => {
    const updated = selectedCoverages.map(coverage =>
      coverage.code === coverageCode
        ? { ...coverage, variant: variantCode }
        : coverage
    );
    onCoverageChange(updated);
  };

  const renderCoverage = (coverage: Coverage, index: number) => {
    const isSelected = selectedCoverages.some(s => s.code === coverage.code);
    const selection = selectedCoverages.find(s => s.code === coverage.code);

    return (
      <div key={index}>
        <Flex vertical gap={12}>
          <CheckboxInput
            id={`${idPrefix}-${coverage.code}`}
            value={isSelected}
            onChange={() => handleCoverageToggle(coverage.code)}
            left={true}
            labelComponent={
              <Flex align="center" gap={8} style={{ flex: 1, marginLeft: 16 }}>
                <Text as="span" type="small" bold>
                  {coverage.label}
                </Text>
                {coverage.isPopular && (
                  <Tag background={colors.green1} value={i18n.t('components.forms.additionalCoverage.popular')} bold />
                )}
              </Flex>
            }
          />
          
          <CoverageDescription type="small" color={colors.text3} dangerouslySetInnerHTML={{ __html: sanitizeHtml(coverage.description) }}/>
          {coverage.variantType === 'select' && isSelected && (
            <Fieldset>
              <Legend>{coverage.label}</Legend>
              <RadioButtonContainer>
                {coverage.variants.map((variant) => (
                  <RadioButton
                    key={variant.code}
                    name={`${idPrefix}-${coverage.code}-variant`}
                    value={variant.code}
                    label={variant.label}
                    checked={selection?.variant === variant.code}
                    onChange={() => handleVariantChange(coverage.code, variant.code)}
                    onKeyDown={onEnterKey(() => handleVariantChange(coverage.code, variant.code))}
                  />
                ))}
              </RadioButtonContainer>
            </Fieldset>
          )}
        </Flex>
      </div>
    );
  };

  return (
    <Container padding={24} radius={12}>
      <Flex vertical gap={24}>
        <Flex vertical gap={8}>
          <Text as="h2" type="xLarge" bold color={colors.text1}>
            {i18n.t('components.forms.additionalCoverage.title')}
          </Text>
          <Text type="small" color={colors.text3}>
            {i18n.t('components.forms.additionalCoverage.subtitle')}
          </Text>
        </Flex>
        
        {coverages.map((coverage, index) => renderCoverage(coverage, index))}
      </Flex>
    </Container>
  );
};
